import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useMember } from "@/contexts/memberContext";
import { useLocationRoomsQuery } from "@/hooks/useLocationRoomsQuery";
import { getPaletteBySwatch } from "@/lib/paletteBySwatch";
import { isBookingRepeating, occursOnDate } from "@/lib/recurrence";
import { parseDate } from "@/lib/utils";
import { orgPartyMemberIds } from "@/utils/orgParty";
import { hasRoomConflict } from "@/utils/roomConflict";
import { BookingService } from "@/services/BookingService";
import { membershipTypeForDisplay, type MembershipType } from "@/types/MembershipType";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { pillLabelFor } from "../BookingTypePill/BookingTypePill";
import System from "../BookingTypeDot/System";
import { BUBBLE_PILL_CLASSES } from "../RoomSchedule/Brick/Bubble";
import { formatDayHeader, getMeetingHeight, getMeetingTop } from "../RoomSchedule/Brick/BrickWeekSchedule.utils";
import { endTimeToMinutes, minutesToHhmm } from "@/utils/bookingTime";
import RoomSwatch from "../RoomSwatch/RoomSwatch";
import type { Booking } from "@/types/Booking";
import type { Location } from "@/types/Location";
import type { Room } from "@/types/Room";

type RoomSelectorProps = {
  date: string | Date | null;
  booking: Booking | null;
  location: Partial<Location> | Location | null;
  room: Partial<Room> | Room | null;
  hoursPadding?: number;
  onRoomChange?: (newRoom: Room | null, hasConflict?: boolean) => void;
};

const RoomSelector = ({ date, booking, location, room, hoursPadding = 1, onRoomChange }: RoomSelectorProps) => {
  const { member } = useMember();
  // The org's own party — moving a booking into a room next to another of the org's own bookings is
  // allowed (buffer waived), exactly as against the same member's. Undefined for non-org users.
  const sameOrgMemberIds = useMemo(() => orgPartyMemberIds(member), [member]);
  // Early return checks BEFORE any hooks
  if (!date || !location || !room || !booking) {
    return null;
  }

  const safeDate = parseDate(date);

  if (!safeDate) {
    return null;
  }

  const [loading, setLoading] = useState(false);
  const [dailyBookings, setDailyBookings] = useState<Booking[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);

  const KEY_WIDTH = 80; // px
  const KEY_HEIGHT = 36; // px for each 30-minute segment

  // Fetch rooms for the location
  const { data: rooms = [], isLoading: roomsLoading } = useLocationRoomsQuery({
    locationSlug: location?.slug ?? "",
    enabled: !!location?.slug,
  });

  // Initialize selected date
  useEffect(() => {
    if (safeDate && !selectedDate) {
      setSelectedDate(new Date(safeDate));
    }
  }, [safeDate]);

  // Initialize current room index
  useEffect(() => {
    if (rooms.length > 0 && room.id) {
      const index = rooms.findIndex((r) => r.id === room.id);
      if (index !== -1) {
        setCurrentRoomIndex(index);
        setSelectedRoom(rooms[index]);
      }
    }
  }, [rooms, room.id]);

  // Fetch bookings when selected room changes
  useEffect(() => {
    const getData = async () => {
      setLoading(true);

      if (!selectedDate || !selectedRoom) {
        setLoading(false);
        return;
      }

      const bookingService = new BookingService();

      // Get bookings for the selected room on the selected date. Pass locationId so the
      // server's role gate uses the calendar-view path and returns OTHER members' bookings
      // in this room — otherwise non-elevated users only see their own.
      const bookings = await bookingService.getWeeklyBookingsForRoom(
        selectedRoom.id!,
        selectedDate,
        1,
        location?.id,
      );

      // occursOnDate handles recurring + non-recurring + the slot fallback (see lib/recurrence).
      // The previous `if (b.recurrence)` short-circuit hid future occurrences of slot bookings
      // whose `recurrence` document was null/missing — silent double-book risk.
      const filteredBookings = bookings.filter((b) => occursOnDate(b, selectedDate));

      setDailyBookings(filteredBookings);
      setLoading(false);
    };
    getData();
  }, [selectedRoom?.id, location?.id, selectedDate?.getTime()]);

  // Parse the booking times to calculate time window
  const bookingStartHour = Math.floor(parseInt(booking.startTime.padStart(4, "0")) / 100);
  const bookingStartMinute = parseInt(booking.startTime.padStart(4, "0")) % 100;
  // A midnight end ("0000") reads as "2400" (see utils/bookingTime).
  const bookingEndHhmm = minutesToHhmm(endTimeToMinutes(booking.endTime, booking.startTime));
  const bookingEndHour = Math.floor(parseInt(bookingEndHhmm) / 100);
  const bookingEndMinute = parseInt(bookingEndHhmm) % 100;

  // Calculate time window
  const windowStartHour = Math.max(0, bookingStartHour - hoursPadding);
  const windowStartMinute = bookingStartMinute;
  const windowStartTime = `${windowStartHour.toString().padStart(2, "0")}${windowStartMinute.toString().padStart(2, "0")}`;

  const windowEndHour = Math.min(23, bookingEndHour + hoursPadding);
  const windowEndMinute = bookingEndMinute;
  const windowEndTime = `${windowEndHour.toString().padStart(2, "0")}${windowEndMinute.toString().padStart(2, "0")}`;

  // Generate 30-minute segments within the time window
  const segments: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, "0")}${m.toString().padStart(2, "0")}`;
      const timeNum = parseInt(time);
      const windowStartNum = parseInt(windowStartTime);
      const windowEndNum = parseInt(windowEndTime);

      if (timeNum >= windowStartNum && timeNum <= windowEndNum) {
        segments.push(time);
      }
    }
  }

  // Room navigation handlers
  const handlePreviousRoom = () => {
    if (currentRoomIndex > 0 && rooms.length > 0) {
      const newIndex = currentRoomIndex - 1;
      setCurrentRoomIndex(newIndex);
      setSelectedRoom(rooms[newIndex]);
    }
  };

  const handleNextRoom = () => {
    if (currentRoomIndex < rooms.length - 1 && rooms.length > 0) {
      const newIndex = currentRoomIndex + 1;
      setCurrentRoomIndex(newIndex);
      setSelectedRoom(rooms[newIndex]);
    }
  };

  // Check if the booking slot conflicts with OTHER bookings in the selected room. Delegates to the
  // pure `hasRoomConflict` helper: same-member bookings may sit adjacent (so a member's own
  // extension in the target room does NOT block the move); the 15-minute buffer applies only across
  // different members.
  const hasConflict = (startTime: string, endTime: string): boolean =>
    hasRoomConflict({ id: booking.id, memberId: booking.memberId, startTime, endTime }, dailyBookings, sameOrgMemberIds);

  // Check conflict and notify parent when room changes
  useEffect(() => {
    if (selectedRoom && selectedRoom.id !== room.id) {
      const conflict = hasConflict(booking.startTime.padStart(4, "0"), booking.endTime.padStart(4, "0"));
      onRoomChange?.(selectedRoom, conflict);
    } else if (selectedRoom && selectedRoom.id === room.id) {
      // Reset to null when returning to original room
      onRoomChange?.(null, false);
    }
  }, [selectedRoom?.id, dailyBookings]);

  if (loading || roomsLoading || !selectedDate) {
    return (
      <div className="border border-gray-200 rounded-lg w-full h-64 bg-gray-100">
        <div className="flex items-center justify-center h-64">
          <Spinner className="size-6 mr-2" />
          <span className="text-lg">Loading room data...</span>
        </div>
      </div>
    );
  }

  const conflict = selectedRoom
    ? hasConflict(booking.startTime.padStart(4, "0"), booking.endTime.padStart(4, "0"))
    : false;
  const isCurrentRoom = selectedRoom?.id === room.id;

  return (
    <div className="relative">
      {/* Navigation Controls */}
      <div className="mb-4">
        {/* Room Navigation */}
        <div className="flex items-center justify-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
          <Button variant="outline" size="sm" onClick={handlePreviousRoom} disabled={currentRoomIndex === 0}>
            <ChevronLeft /> Previous Room
          </Button>
          <div className="flex-1 text-center">
            <p className="text-sm font-semibold text-gray-900">{selectedRoom?.name || "No room selected"}</p>
            <p className="text-xs text-gray-600">
              Room {currentRoomIndex + 1} of {rooms.length}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleNextRoom} disabled={currentRoomIndex === rooms.length - 1}>
            Next Room <ChevronRight />
          </Button>
        </div>
      </div>

      {/* Day View */}
      <div className="border border-gray-200 rounded-lg min-w-0 min-h-[300px] bg-gray-100 overflow-hidden relative">
        <div className="p-2 bg-gray-100 text-lg w-full">
          {selectedRoom && (
            <>
              <RoomSwatch swatch={selectedRoom.swatch ?? ""} name={selectedRoom.name ?? ""} /> - {location.name},{" "}
              {location.city}
            </>
          )}
        </div>

        <div className="flex flex-row">
          {/* Time column on the left */}
          <div className="flex-shrink-0 bg-white">
            <div
              className="text-center border-r border-b bg-gray-50 border-gray-200 border-dotted h-16 pt-4.5"
              style={{ minWidth: `${KEY_WIDTH}px`, maxWidth: `${KEY_WIDTH}px`, width: `${KEY_WIDTH}px` }}
            >
              Time
            </div>

            <div className="flex flex-col">
              {segments.map((time, i) => (
                <div
                  key={i}
                  data-time={time}
                  className={`border-b border-r flex items-center justify-center text-xs font-medium bg-gray-50`}
                  style={{
                    width: `${KEY_WIDTH}px`,
                    height: `${KEY_HEIGHT}px`,
                    minWidth: `${KEY_WIDTH}px`,
                    maxWidth: `${KEY_WIDTH}px`,
                  }}
                >
                  <span className="transform pb-1">{time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Day column */}
          <div className="flex-1 relative">
            {/* Day header */}
            <div className="h-16 pt-4.5 border-r border-b bg-gray-50 border-gray-200 border-dotted text-center font-semibold">
              {formatDayHeader(selectedDate)}
            </div>

            {/* Time segments for this day */}
            <div className="flex flex-col relative">
              {segments.map((time, segIdx) => (
                <div
                  key={segIdx}
                  data-time={time}
                  className="border-b border-r border-gray-200 border-dotted flex items-center justify-center text-xs bg-white"
                  style={{
                    height: `${KEY_HEIGHT}px`,
                    minHeight: `${KEY_HEIGHT}px`,
                    maxHeight: `${KEY_HEIGHT}px`,
                  }}
                >
                  &nbsp;
                </div>
              ))}

              {/* Render existing bookings for this room */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                {dailyBookings.map((bookingItem, bookingIdx) => {
                  const bookingStartNum = parseInt(bookingItem.startTime.padStart(4, "0"));
                  const bookingEndNum = parseInt(minutesToHhmm(endTimeToMinutes(bookingItem.endTime, bookingItem.startTime)));
                  const windowStartNum = parseInt(windowStartTime);
                  const windowEndNum = parseInt(windowEndTime);

                  // Clamp the booking times to the visible window
                  const clampedStartNum = Math.max(bookingStartNum, windowStartNum);
                  const clampedEndNum = Math.min(bookingEndNum, windowEndNum);
                  const clampedStart = clampedStartNum.toString().padStart(4, "0");
                  const clampedEnd = clampedEndNum.toString().padStart(4, "0");

                  const _top = getMeetingTop(clampedStart, KEY_HEIGHT, 0, segments);
                  const _height = getMeetingHeight(clampedStart, clampedEnd, KEY_HEIGHT, 0);
                  const half = parseInt((KEY_HEIGHT / 2).toString(), 10);
                  const _bg = getPaletteBySwatch(selectedRoom?.swatch ?? "")?.transparent || "bg-gray-100/10";

                  const showTopBuffer = bookingItem.hasBuffers && bookingStartNum >= windowStartNum;
                  const showBottomBuffer = bookingItem.hasBuffers && bookingEndNum <= windowEndNum;

                  return (
                    <div
                      key={`${bookingItem.id}-${bookingIdx}`}
                      className={`absolute w-full ${_bg} pointer-events-auto`}
                    >
                      {showTopBuffer && (
                        <div
                          aria-label="15 minute buffer before booking"
                          className={`absolute w-full z-20 rounded-t-md bg-[repeating-linear-gradient(45deg,#d4d4d8_0_1px,transparent_2px_6px)]`}
                          style={{
                            height: `${half}px`,
                            top: `${_top - half}px`,
                          }}
                        >
                          &nbsp;
                        </div>
                      )}
                      {showBottomBuffer && (
                        <div
                          aria-label="15 minute buffer after booking"
                          className={`absolute w-full z-20 rounded-b-md bg-[repeating-linear-gradient(45deg,#d4d4d8_0_1px,transparent_2px_6px)]`}
                          style={{
                            height: `${half}px`,
                            top: `${_top + _height}px`,
                          }}
                        >
                          &nbsp;
                        </div>
                      )}
                      <div
                        className={`absolute w-full z-30 left-0 bg-gradient-to-b ${
                          getPaletteBySwatch(selectedRoom?.swatch ?? "")?.gradientTop
                        } ${
                          getPaletteBySwatch(selectedRoom?.swatch ?? "")?.gradientBottom
                        } text-white text-xs p-1 flex flex-col overflow-hidden`}
                        style={{
                          top: `${_top}px`,
                          height: `${_height}px`,
                        }}
                      >
                        {/*
                          Pills + three truncating rows mirror the day/week calendar bubble
                          (Brick/Bubble.tsx) so the modal reads identically: pills row, time,
                          title. Each text row is `truncate min-w-0` and the block clips via
                          `overflow-hidden`, so short bookings never overlap the time/title.
                        */}
                        <div
                          className="flex justify-start items-center gap-1 shrink-0"
                          aria-label="Booking type and repeat indicator"
                        >
                          {bookingItem.type && (
                            <span
                              className={BUBBLE_PILL_CLASSES}
                              title={membershipTypeForDisplay(bookingItem.type as MembershipType).long}
                              data-testid={`room-selector-type-pill-${bookingItem.id}`}
                            >
                              {pillLabelFor(bookingItem.type as MembershipType | undefined)}
                            </span>
                          )}
                          {isBookingRepeating(bookingItem) && (
                            <span
                              className={BUBBLE_PILL_CLASSES}
                              title="Repeat booking"
                              data-testid={`room-selector-repeat-pill-${bookingItem.id}`}
                            >
                              R
                            </span>
                          )}
                          <System booking={bookingItem} />
                        </div>
                        <span className="mt-1 text-base leading-4 truncate min-w-0 block">
                          {bookingItem.startTime}
                        </span>
                        <span className="text-base leading-4 truncate min-w-0 block">{bookingItem.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Render shadow/mask of the booking being moved */}
              <div className="absolute top-0 left-0 w-full h-full z-40">
                {(() => {
                  const displaySlot = {
                    startTime: booking.startTime.padStart(4, "0"),
                    endTime: bookingEndHhmm,
                  };

                  const bookingStartNum = parseInt(displaySlot.startTime);
                  const bookingEndNum = parseInt(displaySlot.endTime);
                  const windowStartNum = parseInt(windowStartTime);
                  const windowEndNum = parseInt(windowEndTime);

                  // Only show if within visible window
                  if (bookingEndNum <= windowStartNum || bookingStartNum >= windowEndNum) {
                    return null;
                  }

                  // Clamp to visible window
                  const clampedStartNum = Math.max(bookingStartNum, windowStartNum);
                  const clampedEndNum = Math.min(bookingEndNum, windowEndNum);
                  const clampedStart = clampedStartNum.toString().padStart(4, "0");
                  const clampedEnd = clampedEndNum.toString().padStart(4, "0");

                  const _top = getMeetingTop(clampedStart, KEY_HEIGHT, 0, segments);
                  const _height = getMeetingHeight(clampedStart, clampedEnd, KEY_HEIGHT, 0);

                  return (
                    <div
                      className={`absolute w-full rounded-md shadow-md transition-all ${
                        conflict && !isCurrentRoom
                          ? "bg-red-500/60 border-2 border-red-600"
                          : !isCurrentRoom
                            ? "bg-blue-500/60 border-2 border-blue-600"
                            : "bg-gradient-to-b from-indigo-500 to-indigo-600 border-2 border-indigo-700"
                      }`}
                      style={{
                        top: `${_top}px`,
                        height: `${_height}px`,
                        pointerEvents: "auto",
                      }}
                    >
                      <div className="p-2 flex flex-col justify-center h-full text-white select-none">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-semibold flex items-center gap-2">
                              {!isCurrentRoom && conflict && <span className="text-xs">⚠ Conflict</span>}
                              {!isCurrentRoom && !conflict && <span className="text-xs">✓ Available</span>}
                              {isCurrentRoom && <span className="text-xs">📌 Current Room</span>}
                            </div>
                            <div className="text-xs font-medium mt-1">
                              {displaySlot.startTime} - {displaySlot.endTime}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selection Status */}
      {selectedRoom && !isCurrentRoom && (
        <div className="mt-4 p-3 bg-white border-2 border-gray-300 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {conflict ? "⚠ Room Unavailable" : "✓ Room Available"}
              </p>
              <p className="text-xs text-gray-600">
                {booking.startTime} - {booking.endTime} in {selectedRoom.name}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      {!selectedRoom || isCurrentRoom ? (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
          <p className="font-semibold mb-1">How to use:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Use the Previous/Next Room buttons to browse available rooms</li>
            <li>Your booking time will remain the same</li>
            <li>The system checks for conflicts automatically, including 15-minute buffers</li>
            <li>Select a room without conflicts to continue</li>
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default RoomSelector;
